// Many rigs per field, the rule of capture and town lots (M2_SPEC §13).
//   rival drainage: reservoirs with `rivalDrain` lose remaining × (1 − rivalDrainDailyMult) per day to other operators
//   offset wells: on townLot fields with boom > 2, rival derricks go up on the unleased lots over a prospect
//   rule 37 (Texas, flag rule37 1919+): a well on a town lot next to another of your wells needs a $100 (1919 $) permit
//   skidding: moveRig (actions.js) re-uses the rig-up timer with the rig's skidDays
import { makeTaken } from './shapes.js';
import { chance, int, pick } from '../core/rng.js';
import { rivalDrainDailyMult } from '../data/m2/rules.js';
import { fieldDef, eraDollars, stat, eqDef, rigStats, yf } from './defs.js';
import { boomFactor, fieldOpen } from './survey.js';
import { resDef } from './prodm2.js';
import { flagOn } from './m2util.js';
import { notify } from './util.js';

export const OFFSET_PER_DAY = 0.03;
export const OFFSET_BOOM = 2;
export const RULE37_PERMIT = 100; // 1919 $
const OFFSET_RIVALS = ['Guffey & Galey', 'Hogg-Swayne Syndicate', 'Heywood Bros.', 'The Texas Company', 'Gulf Oil', 'Sun Oil', 'Yount-Lee', 'Humble Oil', 'Magnolia Petroleum', 'Joiner & Lloyd', 'Hunt Oil'];

export function tickFieldOps(state) {
  const y = yf(state);
  if (y < 1901) return;
  for (const f of Object.values(state.fields)) {
    for (const [rid, res] of Object.entries(f.reservoirs)) {
      if (res.remaining <= 0) continue;
      const d = resDef(f.id, rid);
      if (!d?.rivalDrain) continue;
      const m = rivalDrainDailyMult(d, y);
      if (m < 1) res.remaining = Math.max(0, res.remaining * m);
    }
    const fdef = fieldDef(f.id);
    if (fdef?.townLot && fieldOpen(state, f.id) && boomFactor(state, f.id) > OFFSET_BOOM) offsetWells(state, f, fdef);
  }
}

function offsetWells(state, f, fdef) {
  let n = 0;
  for (let i = 0; i < f.plots.length; i++) {
    const p = f.plots[i];
    if (p.owned || p.wellId || p.taken || (p.truth?.prospectIdx ?? -1) < 0) continue;
    if (chance(state, OFFSET_PER_DAY)) {
      p.taken = makeTaken({ by: pick(state, OFFSET_RIVALS), day: state.day, drillDay: state.day + int(state, 10, 40), well: null, offset: true });
      n++;
    }
  }
  if (n && !f.offsetNoticeDay) {
    f.offsetNoticeDay = state.day;
    notify(state, 'news', { fieldId: f.id, kind: 'rival', text: `Derricks are going up on every lot at ${fdef.name}. Rival wells next door will drain your pool — lease the neighbours or drill first.` });
  }
}

export function offsetCount(state, fieldId) {
  const f = state.fields[fieldId];
  return f ? f.plots.filter((p) => p.taken?.well && !p.taken.well.dry).length : 0;
}

// Rule 37 exception permit fee for a new well at plotIdx (0 when not needed).
export function rule37Fee(state, fieldId, plotIdx) {
  const fdef = fieldDef(fieldId);
  if (!fdef?.townLot || fdef.regionId !== 'texas' || !flagOn(state, 'rule37')) return 0;
  const f = state.fields[fieldId];
  const p0 = f.plots[plotIdx];
  const near = f.plots.some((p, i) => i !== plotIdx && p.wellId && state.wells[p.wellId] && !['PLUGGED', 'DRY'].includes(state.wells[p.wellId].status)
    && Math.max(Math.abs(p.x - p0.x), Math.abs(p.y - p0.y)) === 1);
  return near ? eraDollars(state, RULE37_PERMIT, 1919) : 0;
}

export const skidDays = (rig) => stat(eqDef(rig.defId), 'skidDays', rigStats(eqDef(rig.defId)).rigUpDays);

export function adjacent(f, a, b) {
  const p = f.plots[a], q = f.plots[b];
  return !!p && !!q && a !== b && Math.max(Math.abs(p.x - q.x), Math.abs(p.y - q.y)) <= 1;
}

// Annual rival drainage rate shown in the field view.
export function rivalDrainPctYr(state, fieldId) {
  const f = state.fields[fieldId];
  if (!f) return 0;
  let best = 0;
  for (const rid of Object.keys(f.reservoirs)) {
    const d = resDef(fieldId, rid);
    if (d?.rivalDrain) best = Math.max(best, 1 - Math.pow(rivalDrainDailyMult(d, yf(state)), 365));
  }
  return Math.round(best * 1000) / 10;
}

// { resId: % of remaining drained per year by other operators } for the field view.
export function rivalDrainByRes(state, fieldId) {
  const out = {};
  for (const rid of Object.keys(state.fields[fieldId]?.reservoirs ?? {})) {
    const d = resDef(fieldId, rid);
    if (d?.rivalDrain) out[rid] = Math.round((1 - Math.pow(rivalDrainDailyMult(d, yf(state)), 365)) * 1000) / 10;
  }
  return out;
}
